/**
 * Hargraves Wellness Theme - Main JavaScript
 * Version: 1.0.0
 */

(function() {
  'use strict';
  
  // ===========================
  // Cart Functionality
  // ===========================
  
  const Cart = {
    // Get current cart state
    get: function() {
      return fetch('/cart.js')
        .then(response => response.json())
        .catch(error => {
          console.error('Error fetching cart:', error);
          return null;
        });
    },
    
    // Add item to cart
    add: function(variantId, quantity = 1, properties = {}) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: properties
        })
      })
      .then(response => response.json())
      .then(data => {
        this.updateCartCount();
        return data;
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        throw error;
      });
    },
    
    // Update cart item quantity
    change: function(lineKey, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity
        })
      })
      .then(response => response.json())
      .then(data => {
        this.updateCartCount();
        return data;
      })
      .catch(error => {
        console.error('Error updating cart:', error);
        throw error;
      });
    },
    
    // Update cart count in header
    updateCartCount: function() {
      this.get().then(cart => {
        if (cart) {
          const cartCountElements = document.querySelectorAll('.cart-count');
          cartCountElements.forEach(el => {
            el.textContent = cart.item_count;
            if (cart.item_count > 0) {
              el.style.display = 'flex';
            } else {
              el.style.display = 'none';
            }
          });
          
          // Trigger custom event
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: cart }
          }));
        }
      });
    }
  };
  
  // Expose Cart object globally
  window.Cart = Cart;
  
  // ===========================
  // Notifications
  // ===========================
  
  const Notifications = {
    show: function(message, type = 'success', duration = 3000) {
      const notification = document.createElement('div');
      notification.className = `notification ${type}`;
      notification.textContent = message;
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease';
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, duration);
    }
  };
  
  window.Notifications = Notifications;
  
  // ===========================
  // Product Form Handler
  // ===========================
  
  function initProductForms() {
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    
    productForms.forEach(form => {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        
        const formData = new FormData(this);
        const button = this.querySelector('[type="submit"]');
        const originalText = button.textContent;
        
        // Disable button
        button.disabled = true;
        button.textContent = 'Adding...';
        
        // Add to cart
        fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        })
        .then(response => response.json())
        .then(data => {
          // Success
          button.textContent = '✓ Added to Cart';
          Cart.updateCartCount();
          Notifications.show('Product added to cart!', 'success');
          
          // Track event
          if (typeof gtag !== 'undefined') {
            gtag('event', 'add_to_cart', {
              currency: data.currency || 'USD',
              value: data.price / 100,
              items: [{
                item_id: data.variant_id,
                item_name: data.product_title,
                price: data.price / 100
              }]
            });
          }
          
          // Reset button after 2 seconds
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 2000);
        })
        .catch(error => {
          // Error
          button.textContent = originalText;
          button.disabled = false;
          Notifications.show('Error adding to cart. Please try again.', 'error');
          console.error('Add to cart error:', error);
        });
      });
    });
  }
  
  // ===========================
  // Accessibility Improvements
  // ===========================
  
  function initAccessibility() {
    // Add keyboard navigation to custom dropdowns
    const dropdowns = document.querySelectorAll('[role="listbox"]');
    dropdowns.forEach(dropdown => {
      dropdown.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.click();
        }
      });
    });
    
    // Skip to content link
    const skipLink = document.querySelector('.skip-to-content-link');
    if (skipLink) {
      skipLink.addEventListener('click', function(e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.focus();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }
  
  // ===========================
  // Lazy Loading Images
  // ===========================
  
  function initLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            observer.unobserve(img);
          }
        });
      });
      
      const images = document.querySelectorAll('img[data-src]');
      images.forEach(img => imageObserver.observe(img));
    }
  }
  
  // ===========================
  // Utility Functions
  // ===========================
  
  // Format money
  window.formatMoney = function(cents, format = '${{amount}}') {
    const value = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', value);
  };
  
  // Debounce function
  window.debounce = function(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };
  
  // ===========================
  // Initialize on DOM Ready
  // ===========================
  
  function init() {
    initProductForms();
    initAccessibility();
    initLazyLoading();
    Cart.updateCartCount();
    
    console.log('Hargraves Wellness Theme v1.0.0 loaded');
  }
  
  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // ===========================
  // Shopify Theme Editor Support
  // ===========================
  
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:unload', init);
    document.addEventListener('shopify:section:select', init);
    document.addEventListener('shopify:section:deselect', init);
    document.addEventListener('shopify:block:select', init);
    document.addEventListener('shopify:block:deselect', init);
  }
  
})();
